import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Container, Footer, Header, Navbar } from '@components';
import Blog from '@components/cards/blog';
import { useInView } from 'framer-motion';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useEffect, useRef, useState } from 'react';
import { FiLoader } from 'react-icons/fi';

import { getCompanyBlogs } from '@api/blogs';
import { PostOrPage } from '@tryghost/content-api';

interface BlogsPageProps {
  initialBlogs: PostOrPage[];
  hasMore: boolean;
}

const PAGE_SIZE = 10;

export default function BlogsPage({ initialBlogs, hasMore }: BlogsPageProps) {
  const [blogs, setBlogs] = useState<PostOrPage[]>(initialBlogs);
  const [hasMoreBlogs, setHasMoreBlogs] = useState(hasMore);
  const [isLoading, setIsLoading] = useState(false);

  const ref = useRef(null);
  const isInView = useInView(ref);

  async function handleLoadMore() {
    if (!hasMoreBlogs || isLoading || (blogs && blogs.length < 0)) return;
    setIsLoading(true);

    try {
      const response = await getCompanyBlogs(
        blogs.length / PAGE_SIZE + 1,
        PAGE_SIZE
      );
      setBlogs([...blogs, ...response.posts]);
      setHasMoreBlogs(
        response.meta.pagination.pages > blogs.length / PAGE_SIZE + 1
      );
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    handleLoadMore();
  }, [isInView, hasMoreBlogs, isLoading]);

  return (
    <Container>
      <Head>
        <title>Business and Brand Stories | Jhattse</title>
        <meta
          name="description"
          content="Business Stories covering unique Brands, Business persons and solopreneurs from India and globe to you on Jhattse ecommerce. Watch out stories as they unfold"
        />
      </Head>

      <Header />

      <main className="max-w-[1240px] w-full mx-auto font-manrope">
        <div className="hidden py-8 px-2 md:flex items-center text-base">
          <Breadcrumb className="text-base text-neutral-400 py-4 font-medium font-manrope list-none text-breadcrumbs">
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem
              className="text-ellipsis text-left break-words line-clamp-1 select-none text-neutral-800 font-semibold"
              isCurrentPage
            >
              <BreadcrumbLink>Business and Brand Stories</BreadcrumbLink>
              <ol className="p-0 list-none"></ol>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>

        <div className="w-full px-2 h-fit hidden md:flex py-4 space-y-4">
          <h1 className="text-xl text-neutral-800 font-extrabold">
            Business and Brand Stories from Jhattse
          </h1>
        </div>

        <div className="py-8 px-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
          {blogs && blogs.length > 0 ? (
            blogs?.map((blog: PostOrPage) => (
              <Blog
                key={blog.id}
                blog={blog}
                readMoreURL={`business-and-brand-stories/${blog.slug}`}
              />
            ))
          ) : (
            <span className="text-lg text-neutral-800 font-semibold">
              No blogs found. Check back later
            </span>
          )}

          {isLoading && hasMoreBlogs && (
            <div className="px-12 py-6 flex justify-center">
              <FiLoader className="w-8 h-8 text-neutral-400 animate-spin" />
            </div>
          )}

          <div ref={ref} className="h-20" />
        </div>
      </main>
      <Navbar />
      <Footer />
    </Container>
  );
}

export const getServerSideProps: GetServerSideProps = async () => {
  const response = await getCompanyBlogs(1, PAGE_SIZE);

  return {
    props: {
      initialBlogs: response.posts,
      hasMore: response.meta.pagination.pages > 1,
    },
  };
};
